import { useEffect, useRef } from 'react';

/**
 * Hook for polling recommendation generation status.
 *
 * Polls every 3 seconds while isPolling is true.
 * Automatically cleans up interval on unmount or when polling stops.
 *
 * Pattern reference: useChildSummary.ts lines 64-85
 *
 * @param isPolling - Whether to actively poll
 * @param onPoll - Callback function to execute on each poll (async supported)
 * @param pollInterval - Polling interval in milliseconds (default: 3000)
 */
export function useRecommendationPolling(
  isPolling: boolean,
  onPoll: () => void | Promise<void>,
  pollInterval = 3000
): void {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Start polling if enabled
    if (isPolling) {
      intervalRef.current = setInterval(async () => {
        try {
          await onPoll();
        } catch (error) {
          // Polling errors are silent — keep trying
          console.debug('[useRecommendationPolling] Poll error (will retry):', error);
        }
      }, pollInterval);
    }

    // Cleanup on unmount or when polling stops
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPolling, onPoll, pollInterval]);
}
